import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SafeImage from '../components/SafeImage';
import { Check, X, ArrowRight, Info } from 'lucide-react';
import { PLANS } from '../mock/mockData';
import { useAuth } from '../context/AuthContext';

const Feature = ({ name, free, platinum, immortal, radiant }) => (
  <tr className="border-t border-zinc-800">
    <td className="py-3 px-4 text-sm text-zinc-300">{name}</td>
    <td className="py-3 px-4 text-center">{free ? <Check className="w-4 h-4 text-cyan-300 mx-auto"/> : <X className="w-4 h-4 text-zinc-600 mx-auto"/>}</td>
    <td className="py-3 px-4 text-center">{platinum ? <Check className="w-4 h-4 text-cyan-300 mx-auto"/> : <X className="w-4 h-4 text-zinc-600 mx-auto"/>}</td>
    <td className="py-3 px-4 text-center">{immortal ? <Check className="w-4 h-4 text-orange-300 mx-auto"/> : <X className="w-4 h-4 text-zinc-600 mx-auto"/>}</td>
    <td className="py-3 px-4 text-center">{radiant ? <Check className="w-4 h-4 text-yellow-300 mx-auto"/> : <X className="w-4 h-4 text-zinc-600 mx-auto"/>}</td>
  </tr>
);

const FeatureGroup = ({ name }) => (
  <tr className="border-t border-zinc-800 bg-cyan-950/10">
    <td colSpan={5} className="py-3 px-4 text-xs uppercase tracking-widest text-cyan-300 font-semibold">
      {name}
    </td>
  </tr>
);

const PriceLabel = ({ label, size = 'text-4xl' }) => {
  const [amount, currency] = label.split(' ');
  return (
    <span className={`font-mono-display ${size} neon-orange inline-flex flex-col leading-none`}>
      <span>{amount}</span>
      <span>{currency}</span>
    </span>
  );
};

export default function Pricing() {
  const { isAuthed } = useAuth();

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">// pricing matrix</div>
          <h1 className="font-mono-display text-4xl text-white">Unleash the <span className="neon-orange">Power</span> of ValoRex</h1>
          <p className="text-zinc-400 mt-3">All plans are billed manually in PKR. No automated gateways. Admin verifies your payment and activates instantly.</p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-14">
          {PLANS.map((p) => {
            const cardGlow = p.id === 'platinum' ? 'pricing-card-blue' : p.id === 'immortal' ? 'pricing-card-red' : p.id === 'radiant' ? 'pricing-card-gold' : 'neon-card';
            return (
            <div key={p.id} className={`${cardGlow} rounded-lg p-6 relative h-full flex flex-col`}>
              {p.id==='immortal' && (
                <span className="absolute -top-3 left-6 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-orange-500 text-black rounded">Best Value</span>
              )}
              <div className="flex items-center gap-3 mb-4">
                <SafeImage src={p.icon} alt={p.name} className="w-12 h-12 object-contain" fallbackClassName="w-12 h-12" fallbackIcon={Info} />
                <div>
                  <div className="font-mono-display text-2xl text-white">{p.name}</div>
                  <div className="text-xs text-zinc-400">{p.tagline}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 mb-5">
                <PriceLabel label={p.priceLabel} />
                <span className="text-sm text-zinc-400">/ month</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">{p.slots} account slots</div>
              <ul className="space-y-2 text-sm text-zinc-300 mb-6">
                {p.features.map((f)=> (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-300 mt-0.5"/>{f}</li>
                ))}
              </ul>
              <Link
                to={isAuthed ? (p.id === 'free' ? '/dashboard' : `/billing/${p.id}`) : (p.id === 'free' ? '/auth/register' : `/billing/${p.id}`)}
                className="btn-neon w-full justify-center mt-auto"
              >
                {isAuthed && p.id === 'free' ? 'Dashboard' : p.cta} <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
            );
          })}
        </div>

        {/* Compare */}
        <div className="neon-card rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
            <Info className="w-4 h-4 text-cyan-300"/>
            <h3 className="font-mono-display text-lg text-white">Feature Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-zinc-400">
                  <th className="py-3 px-4 text-left">Feature</th>
                  <th className="py-3 px-4">Free</th>
                  <th className="py-3 px-4">Platinum</th>
                  <th className="py-3 px-4">Immortal</th>
                  <th className="py-3 px-4">Radiant</th>
                </tr>
              </thead>
              <tbody>
                <Feature name="Account Details" free platinum immortal radiant />
                <Feature name="Inventory &amp; Store access" free platinum immortal radiant />
                <Feature name="Shareable Account Links" free platinum immortal radiant />
                <Feature name="Account Agents view" free={false} platinum={false} immortal radiant />
                <Feature name="Control Panel" free={false} platinum={false} immortal radiant />
                <FeatureGroup name="Control Panel Privacy Features" />
                <Feature name="Hide IGN on shared page" free={false} platinum={false} immortal radiant />
                <Feature name="Hide inventory on shared page" free={false} platinum={false} immortal radiant />
                <Feature name="Hide VP spent and prices" free={false} platinum={false} immortal radiant />
                <Feature name="Hide store from shared page" free={false} platinum={false} immortal radiant />
                <Feature name="Hide match history" free={false} platinum={false} immortal radiant />
                <Feature name="Deactivate shared links" free={false} platinum={false} immortal radiant />
                <Feature name="Regenerate share links" free={false} platinum={false} immortal radiant />
                <Feature name="Hall of Fame visibility control" free={false} platinum={false} immortal radiant />
                <Feature name="Decreased Cooldown" free={false} platinum immortal radiant />
                <Feature name="No Cooldown" free={false} platinum={false} immortal={false} radiant />
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </Layout>
  );
}
